import { useMemo } from 'react';

import { useFeature } from '@/features/feature-flags';
import { FEATURES } from '@/features/feature-flags/feature-flags.constants';

import { useGroupsQuery } from '../groups/groups.query';
import { PEOPLE_STALE_TIME_MS, usePeopleWikiQuery } from '../people-wiki.query';

import { createPersonMentionLinker } from './person-mention-links';

/** The text with `@<person name>` and `@<group name>` rewritten into links. Returns the text as is when it can't. */
export const usePersonMentionLinks = (
  text: string,
  { enabled }: { enabled: boolean }
) => {
  const hasFeature = useFeature();
  const isActive =
    enabled && hasFeature(FEATURES.PEOPLE_WIKI) && text.includes('@');
  const { data: people } = usePeopleWikiQuery({ enabled: isActive });
  // Saved groups are tagged only where messages to people are on, as in the composer.
  // Every message with an @ reads this, so it stays fresh like the people list
  const hasGroups = hasFeature(FEATURES.HIVE);
  const { data: savedGroups } = useGroupsQuery({
    enabled: isActive && hasGroups,
    staleTime: PEOPLE_STALE_TIME_MS,
  });
  // A disabled query still returns what it cached, so the flag gates the list too
  const groups = hasGroups ? savedGroups : undefined;
  const linkMentions = useMemo(
    () =>
      people || groups ? createPersonMentionLinker(people ?? [], groups) : null,
    [people, groups]
  );

  return isActive && linkMentions ? linkMentions(text) : text;
};
